import { CircleDashed, DotOutline } from "@phosphor-icons/react";

import RenderIf from "common/components/RenderIf";
import {
  DropdownMenu,
  DropdownMenuCheckbox,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FilterButton from "common/components/ui/FilterButton";
import { CAMPAIGN_STATES } from "common/constants";
import { typedObjectEntries } from "common/helpers/utils";
import type { CampaignState } from "common/types";

interface CampaignStateSelectorProps {
  selection: CampaignState[];
  setSelection: (selection: CampaignState[]) => void;
}

export default function CampaignStateSelector({
  selection,
  setSelection,
}: CampaignStateSelectorProps) {
  const campaignStates = typedObjectEntries(CAMPAIGN_STATES);

  function removeState(state: CampaignState) {
    setSelection(selection.filter((s) => s !== state));
  }
  function addState(state: CampaignState) {
    setSelection([...selection, state]);
  }

  let triggerLabel = "All statuses";

  if (selection.length > 0 && selection.length !== campaignStates.length) {
    if (selection.length === 1) {
      triggerLabel = CAMPAIGN_STATES[selection[0]].label;
    } else {
      triggerLabel = `${selection.length} statuses`;
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <FilterButton
          leftIcon={<CircleDashed weight="fill" />}
          className="w-43 lg:w-48"
        >
          {triggerLabel}
        </FilterButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="min-w-[var(--radix-popper-anchor-width)]">
        <DropdownMenuList>
          <DropdownMenuItem onSelect={() => setSelection([])}>
            All statuses
          </DropdownMenuItem>
          {campaignStates.map(([state, { label, iconColor }]) => {
            const isChecked = selection.includes(state);

            return (
              <DropdownMenuItem
                key={state}
                onSelect={() => {
                  setSelection([state]);
                }}
              >
                <RenderIf condition={true}>
                  <DropdownMenuCheckbox
                    isChecked={isChecked}
                    onCheckedChange={() => {
                      if (isChecked) {
                        removeState(state);
                      } else {
                        addState(state);
                      }
                    }}
                  />
                </RenderIf>

                <DotOutline size={20} weight="fill" className={iconColor} />

                <span className="line-clamp-1 break-all">{label}</span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
